import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { FeatureFlag } from 'app/api/services/site-configs/types';

import { SsrModule } from 'app/constants/ssrModules';
import { Icon } from 'app/design-system/components/Icon';
import { TabByType, Tabs, TabsSize } from 'app/design-system/components/Tabs';
import { useTranslation } from 'app/lang';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { useMenuItemsByFeatureFlags } from 'app/lib/feature-flags/useMenuItemsByFeatureFlags';
import { ROUTE_PATH } from 'app/lib/routing/constants';
import { headerMenuItemsWithPathname } from 'app/modules/Layout/Header/selectors';
import { SupportButton } from 'app/modules/Layout/Header/SupportButton';
import { getDictionaryDataItems } from 'app/modules/Navigation/utils';
import { SEARCH_MODAL_PARAM_KEY } from 'app/modules/Search/constants';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';
import { BrandLogo } from 'app/widgets/BrandLogo';

import SearchModal from '../../Search/Modal';
import { AccountBalance } from './AccountBalance';
import AuthBlock from './AuthBlock/AuthBlock';
import LanguageSelector from './LanguageSelector';
import Menu from './Menu/Menu';
import { MobileAppIcons } from './MobileAppIcons';
import { NotificationsBell } from './NotificationsBell';

import dictionary from './Header.dictionary.json';

import styles from './Header.module.scss';

const Header: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isAllowSupport = useFeatureFlag(FeatureFlag.ALLOW_SUPPORT);
  const isAllowMobileAppIcons = useFeatureFlag(FeatureFlag.ALLOW_MOBILE_APP_ICONS);
  const isSignedIn = useSelector(selectIsLoggedIn);

  const filteredTabItems = useMenuItemsByFeatureFlags({
    fn: headerMenuItemsWithPathname,
    pathname,
  });

  const tabs = filteredTabItems.map((item) => ({
    id: item.key,
    to: item.location,
    title: getDictionaryDataItems(t, item?.title ? item.title : item.label),
    active: item.active,
    label: (
      <span className={styles.label}>
        {getDictionaryDataItems(t, item.label)}
      </span>
    ),
    dataGaValues: item.dataGaValues,
  }));

  const onChangeLocationHandler = useCallback((locationId: string, tab?: TabByType<string>) => {
    const isLinkTab = tab && 'to' in tab;
    const path = isLinkTab ? tab.to : locationId;

    if (pathname !== path) {
      return;
    }

    window.location.reload();
  }, [pathname]);

  const onLogoClickHandler = useCallback(() => {
    onChangeLocationHandler(ROUTE_PATH.ROOT);
  }, [onChangeLocationHandler]);

  return (
    <div data-ssr-id={SsrModule.HEADER}
         className={styles.headerWrapper}
         data-test="main-nav__wrapper">
      <div className={styles.header}>
        <Link
            to={ROUTE_PATH.ROOT}
            className={styles.companyLogo}
            data-test="company-logo"
            data-ga-event="header-logo"
            arial-label={t(dictionary.logoImg)}
            onClick={onLogoClickHandler}>
          <BrandLogo />
        </Link>

        {isAllowMobileAppIcons && (
          <MobileAppIcons className={styles.mobileAppIcons} />
        )}

        <Tabs type="link"
              size={TabsSize.MEDIUM}
              tabs={tabs}
              disableActiveTab={false}
              className={styles.tabs}
              onChange={onChangeLocationHandler} />

        <div className={styles.userInteractsWrapper}>
          <LanguageSelector />
          <div className={styles.userInteractsContainer}>
            <Link className={styles.btn}
                  data-test="search-button"
                  data-ga-event="header-search"
                  to={`?${SEARCH_MODAL_PARAM_KEY}`}>
              <Icon icon="search" />
            </Link>

            {isAllowSupport && <SupportButton className={styles.btn} /> }

            {isSignedIn && (
              <NotificationsBell />
            )}

          </div>

          {isSignedIn && (
            <AccountBalance />
          )}
          {!isSignedIn && (
            <AuthBlock />
          )}
          <Menu />
        </div>
      </div>
      <SearchModal />
    </div>
  );
};

export default Header;
